import { Head, Link } from '@inertiajs/react';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { AvatarCircle } from '@/components/admin/avatar-circle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { AdminTracksShow } from '@/types';
import { githubUsername } from '@/components/admin/github-username';

export default function Show({
  track,
  teams,
  eventSlug,
  title,
}: AdminTracksShow) {
  return (
    <>
      <Head title={title} />
      <div className="container mx-auto space-y-6 py-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link href={`/admin/${eventSlug}/tracks`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Tracks
            </Link>
          </Button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-bold text-3xl tracking-tight">{track.name}</h1>
            {track.description && (
              <p className="mt-2 text-muted-foreground">{track.description}</p>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Teams ({teams.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {teams.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p>No teams have selected this track yet.</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team</TableHead>
                      <TableHead>Mentor</TableHead>
                      <TableHead>Members</TableHead>
                      <TableHead>Table #</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teams.map((team) => (
                      <TableRow key={team.id}>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="font-medium">{team.slug}</span>
                            {team.formedOnSite && (
                              <Badge variant="secondary" className="w-fit">
                                Formed on-site
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {team.mentor ? (
                            <a
                              href={team.mentor.github}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                            >
                              <AvatarCircle
                                src={team.mentor.avatarUrl}
                                name={team.mentor.fullName}
                                size="sm"
                              />
                              {team.mentor.fullName}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-2">
                            {team.members.length === 0 ? (
                              <span className="text-muted-foreground text-sm">
                                No members
                              </span>
                            ) : (
                              team.members.map((member) => (
                                <div
                                  key={member.id}
                                  className="flex items-center gap-2"
                                >
                                  <AvatarCircle
                                    src={member.avatarUrl}
                                    name={member.fullName}
                                    size="sm"
                                  />
                                  <Link
                                    href={`/admin/${eventSlug}/${githubUsername(member.github)}`}
                                    className="text-sm hover:underline"
                                  >
                                    {member.fullName}
                                  </Link>
                                  {member.github && (
                                    <a
                                      href={member.github}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-blue-600 text-xs hover:text-blue-800"
                                    >
                                      @{githubUsername(member.github)}
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {team.tableNumber || (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" asChild>
                            <Link href={`/admin/${eventSlug}/teams/${team.slug}`}>
                              View Team
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
